import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'

import styles from '../../styles/components/LegalPage.module.css'

// Shared shell for the /legal pages: dark marketing look (navy, Poppins,
// green accents) with the marketing top bar and footer. The mobile app opens
// these pages in a web view with ?enable-layout=false, and there the top bar
// and footer are left out so the app is not showing a website menu.
//
// Colors, radii and the font stack mirror the design-system tokens in
// public/index-new/_ds (styles/components/LegalPage.module.css carries the
// values as fallbacks, so no token stylesheet needs loading here).

export const LEGAL_PAGES = [
  { href: '/legal/privacy-policy', label: 'Privacy Policy' },
  { href: '/legal/terms-of-use', label: 'Terms of Service' },
  { href: '/legal/accessibility', label: 'Accessibility' }
]

// `eyebrow`, `updated`, `tabs` and `address` let a public page that is not one
// of the three legal texts (the agreement verify page) wear the same shell.
export default function LegalPage ({ title, description, updated, eyebrow = 'Legal', tabs = true, address = true, children }) {
  const router = useRouter()
  const embedded = router.query['enable-layout'] === 'false'
  const current = router.pathname.replace(/\/$/, '')

  return (
    <>
      <Head>
        <title>{`${title} | Ivy Flip`}</title>
        <meta
          name='description'
          content={description}
        />
      </Head>

      <div className={styles.page}>
        {!embedded && (
          <div className={styles.nav}>
            <div className={`${styles.inner} ${styles.navInner}`}>
              <a
                href='/'
                className={styles.logo}
              >
                <img
                  src='/index-new/assets/ivy-logo-white.svg'
                  alt='Ivy Flip'
                />
              </a>
              <div className={styles.navLinks}>
                <a
                  href='/pricing/'
                  className={styles.navLink}
                >
                  Pricing
                </a>
                <Link
                  href='/app/signin'
                  prefetch={false}
                  className={styles.navLink}
                >
                  Log in
                </Link>
                <a
                  href='/retailers/'
                  className={styles.navRetailers}
                >
                  For Retailers
                </a>
              </div>
            </div>
          </div>
        )}

        <header className={`${styles.inner} ${styles.hero}`}>
          <p className={styles.eyebrow}>{eyebrow}</p>
          <h1 className={styles.title}>{title}</h1>
          {updated && <p className={styles.updated}>Last updated {updated}</p>}
        </header>

        {tabs && (
          <nav
            className={styles.inner}
            aria-label='Legal pages'
          >
            <div className={styles.tabs}>
              {LEGAL_PAGES.map(page => (
                <Link
                  key={page.href}
                  href={page.href}
                  prefetch={false}
                  className={`${styles.tab} ${current === page.href ? styles.tabActive : ''}`}
                  aria-current={current === page.href ? 'page' : undefined}
                >
                  {page.label}
                </Link>
              ))}
            </div>
          </nav>
        )}

        <main className={`${styles.inner} ${styles.content}`}>
          {address && (
            <address className={styles.address}>
              <strong>Ivy Growth, Inc.</strong> (doing business as Ivy Flip)<br />
              44 S 17th St<br />
              Allentown, PA 18104<br />
              Contact Email: <a href='mailto:hello@ivyflip.com'>hello@ivyflip.com</a>
            </address>
          )}
          {children}
        </main>

        {!embedded && (
          <footer className={styles.footer}>
            <div className={`${styles.inner} ${styles.footerInner}`}>
              <div className={styles.footerRow}>
                <div className={styles.footerBrand}>
                  <img
                    src='/index-new/assets/ivy-logo-white.svg'
                    alt='Ivy Flip'
                  />
                  <span className={styles.footerTagline}>Creating opportunities for people.</span>
                </div>
                <div className={styles.footerLinks}>
                  <a
                    href='/'
                    className={styles.footerLink}
                  >
                    Home
                  </a>
                  <a
                    href='/about/'
                    className={styles.footerLink}
                  >
                    About
                  </a>
                  <a
                    href='/pricing/'
                    className={styles.footerLink}
                  >
                    Pricing
                  </a>
                  <Link
                    href='/app/signin'
                    prefetch={false}
                    className={styles.footerLink}
                  >
                    Log in
                  </Link>
                </div>
              </div>
              <div className={`${styles.footerRow} ${styles.footerRowBottom}`}>
                <div className={styles.footerLinks}>
                  <a
                    href='https://www.instagram.com/ivyfliphustle'
                    target='_blank'
                    rel='noopener noreferrer'
                    className={styles.footerLink}
                  >
                    Instagram
                  </a>
                  <a
                    href='https://www.youtube.com/@ivyflip'
                    target='_blank'
                    rel='noopener noreferrer'
                    className={styles.footerLink}
                  >
                    YouTube
                  </a>
                  <a
                    href='https://www.tiktok.com/@ivyflip.com'
                    target='_blank'
                    rel='noopener noreferrer'
                    className={styles.footerLink}
                  >
                    TikTok
                  </a>
                </div>
                <div className={styles.footerLegal}>
                  {LEGAL_PAGES.map(page => (
                    <Link
                      key={page.href}
                      href={page.href}
                      prefetch={false}
                      className={styles.footerLink}
                    >
                      {page.label}
                    </Link>
                  ))}
                  <span className={styles.copyright}>© {new Date().getFullYear()} Ivy Growth, Inc.</span>
                </div>
              </div>
            </div>
          </footer>
        )}
      </div>
    </>
  )
}
